import React, { useEffect, useRef, useState } from 'react';
import './Header.css';
import './AuthModal.css';
import KeepMyAssetLogo from './KeepMyAssetLogo';
import Modal from 'react-modal';
import { Link } from 'react-router-dom';
import { LuCheck, LuShieldCheck } from 'react-icons/lu';

const GOOGLE_MODAL_BUTTON_ID = 'google-sign-in-modal-container';

const Header = ({
  user,
  onLogout,
  onGoogleResponse,
  googleClientId,
  onNavigateHome,
  onNavigateNomineeCheck,
  currentPage,
}) => {
  const isGoogleInitialized = useRef(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isGoogleButtonRendered, setIsGoogleButtonRendered] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const initializeGoogleButton = () => {
      if (!window.google || !googleClientId || isGoogleInitialized.current) return false;
      try {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: onGoogleResponse,
        });
        isGoogleInitialized.current = true;
        return true;
      } catch (err) {
        console.error('Failed to initialize Google Sign-In:', err);
      }
      return false;
    };

    if (initializeGoogleButton()) return;

    const script = document.querySelector('script[src^="https://accounts.google.com/gsi/client"]');
    const onScriptLoad = () => {
      initializeGoogleButton();
    };
    if (script) {
      script.addEventListener('load', onScriptLoad);
    }

    const startTime = Date.now();
    const pollIntervalId = window.setInterval(() => {
      if (isGoogleInitialized.current) {
        window.clearInterval(pollIntervalId);
        return;
      }
      if (Date.now() - startTime > 7000) {
        window.clearInterval(pollIntervalId);
        return;
      }
      initializeGoogleButton();
    }, 200);

    return () => {
      if (script) script.removeEventListener('load', onScriptLoad);
      window.clearInterval(pollIntervalId);
    };
  }, [googleClientId, onGoogleResponse]);

  useEffect(() => {
    try {
      const root = document.getElementById('root');
      if (root) Modal.setAppElement(root);
    } catch (_) {}
  }, []);

  useEffect(() => {
    const openModalListener = () => setIsAuthModalOpen(true);
    window.addEventListener('open-auth-modal', openModalListener);
    return () => window.removeEventListener('open-auth-modal', openModalListener);
  }, []);

  useEffect(() => {
    if (!isAuthModalOpen) return;
    const renderGoogleBtn = () => {
      try {
        if (!window.google?.accounts?.id) return false;
        const container = document.getElementById(GOOGLE_MODAL_BUTTON_ID);
        if (!container) return false;
        container.innerHTML = '';
        window.google.accounts.id.renderButton(container, {
          theme: 'outline',
          size: 'large',
          width: 280,
          text: 'signin_with',
          shape: 'pill',
        });
        setIsGoogleButtonRendered(true);
        return true;
      } catch (err) {
        console.error('Failed to render Google Sign-In button:', err);
        return false;
      }
    };
    setIsGoogleButtonRendered(false);
    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (renderGoogleBtn() || attempts >= 12) {
        window.clearInterval(intervalId);
      }
    }, 250);
    return () => window.clearInterval(intervalId);
  }, [isAuthModalOpen, googleClientId]);

  useEffect(() => {
    if (user && isAuthModalOpen) {
      setIsAuthModalOpen(false);
    }
  }, [user, isAuthModalOpen]);

  useEffect(() => {
    document.body.classList.toggle('mobile-nav-open', isMobileMenuOpen);
    return () => document.body.classList.remove('mobile-nav-open');
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const renderGoogleFallback = () => {
    try {
      if (window.google?.accounts?.id) {
        const c = document.getElementById(GOOGLE_MODAL_BUTTON_ID);
        if (c) {
          c.innerHTML = '';
          window.google.accounts.id.renderButton(c, {
            theme: 'outline',
            size: 'large',
            width: 280,
            text: 'signin_with',
            shape: 'pill',
          });
          setIsGoogleButtonRendered(true);
        }
      }
    } catch (_) {}
  };

  return (
    <>
      <header className="header">
        <button className="brand-button" type="button" onClick={onNavigateHome} aria-label="KeepMyAsset home">
          <KeepMyAssetLogo />
        </button>

        {!user && (
          <nav className="public-nav public-nav-desktop" aria-label="Main navigation">
            <Link to="/#features">Features</Link>
            <Link to="/how-it-works">How it works</Link>
            <Link to="/security">Security</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/about">About</Link>
            <a href="https://wa.me/918318959079" target="_blank" rel="noopener noreferrer">Contact</a>
          </nav>
        )}

        {user && (
          <nav className="header-nav header-nav-desktop">
            <button
              className={`nav-btn ${currentPage === 'home' ? 'active' : ''}`}
              onClick={onNavigateHome}
            >
              Dashboard
            </button>
            <button
              className={`nav-btn ${currentPage === 'nominee-check' ? 'active' : ''}`}
              onClick={onNavigateNomineeCheck}
            >
              Check Nominations
            </button>
          </nav>
        )}

        <div className="auth-section auth-section-desktop">
          {!user && (
            <>
              <button type="button" className="header-signin" onClick={() => setIsAuthModalOpen(true)}>
                Login
              </button>
              <button type="button" className="header-cta" onClick={() => setIsAuthModalOpen(true)}>
                Start Free
              </button>
            </>
          )}

          {user && (
            <div className="user-info-container">
              {user.picture && (
                <img src={user.picture} alt={user.name} className="user-avatar" />
              )}
              <div className="user-details">
                <div className="user-name">{user.name}</div>
                <div className="user-email">{user.email}</div>
              </div>
              <span onClick={onLogout} className="logout-link" role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onLogout()}>
                Logout
              </span>
            </div>
          )}
        </div>

        <button
          className={`menu-toggle ${isMobileMenuOpen ? 'is-open' : ''}`}
          type="button"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-navigation"
          onClick={() => setIsMobileMenuOpen((open) => !open)}
        >
          <span /><span /><span />
        </button>

        <nav
          id="mobile-navigation"
          className={`mobile-nav ${isMobileMenuOpen ? 'is-open' : ''}`}
          aria-label="Mobile navigation"
        >
          {!user ? (
            <>
              <Link onClick={closeMobileMenu} to="/#features">Features</Link>
              <Link onClick={closeMobileMenu} to="/how-it-works">How it works</Link>
              <Link onClick={closeMobileMenu} to="/security">Security</Link>
              <Link onClick={closeMobileMenu} to="/faq">FAQ</Link>
              <Link onClick={closeMobileMenu} to="/about">About</Link>
              <a href="https://wa.me/918318959079" target="_blank" rel="noopener noreferrer" onClick={closeMobileMenu}>
                Contact
              </a>
              <button className="mobile-menu-signin" type="button" onClick={() => { closeMobileMenu(); setIsAuthModalOpen(true); }}>
                Login
              </button>
              <button className="mobile-menu-cta" type="button" onClick={() => { closeMobileMenu(); setIsAuthModalOpen(true); }}>
                Start Free
              </button>
            </>
          ) : (
            <>
              <button
                className={`mobile-nav-btn ${currentPage === 'home' ? 'active' : ''}`}
                type="button"
                onClick={() => { onNavigateHome(); closeMobileMenu(); }}
              >
                Dashboard
              </button>
              <button
                className={`mobile-nav-btn ${currentPage === 'nominee-check' ? 'active' : ''}`}
                type="button"
                onClick={() => { onNavigateNomineeCheck(); closeMobileMenu(); }}
              >
                Check Nominations
              </button>
              <button className="mobile-menu-logout" type="button" onClick={() => { onLogout(); closeMobileMenu(); }}>
                Logout
              </button>
            </>
          )}
        </nav>

        <Modal
          isOpen={isAuthModalOpen}
          onRequestClose={() => setIsAuthModalOpen(false)}
          contentLabel="Sign in"
          className="auth-modal"
          overlayClassName="auth-modal-overlay"
          style={{
            content: {
              position: 'relative',
              inset: 'unset',
              top: 'unset',
              left: 'unset',
              right: 'unset',
              bottom: 'unset',
              transform: 'none',
              margin: 0,
              padding: 0,
              border: 'none',
            },
          }}
        >
          <button
            type="button"
            className="auth-modal-close"
            aria-label="Close"
            onClick={() => setIsAuthModalOpen(false)}
          >
            ×
          </button>
          <div className="auth-modal-hero">
            <div className="auth-modal-icon">
              <LuShieldCheck aria-hidden="true" />
            </div>
            <h2>Your family asset vault — start free</h2>
            <p>
              Sign in with Google to record assets, add nominees, and keep everything organized in one secure place.
            </p>
          </div>
          <div className="auth-modal-body">
            <ul className="auth-modal-benefits">
              <li><LuCheck aria-hidden="true" /> Add bank, PPF, EPF, stocks, insurance &amp; more</li>
              <li><LuCheck aria-hidden="true" /> Map nominees so your family knows where to begin</li>
              <li><LuCheck aria-hidden="true" /> Encrypted storage — your data stays yours</li>
            </ul>
            <div id={GOOGLE_MODAL_BUTTON_ID} className="auth-modal-google" />
            {!isGoogleButtonRendered && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button type="button" className="auth-modal-fallback-btn" onClick={renderGoogleFallback}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.5 33.6 29.1 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C33.7 6.1 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c10.4 0 19-8.4 19-19 0-1.3-.1-2.2-.4-3.5z"/>
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.8 16.4 19 14 24 14c3 0 5.7 1.1 7.8 3l5.7-5.7C33.7 6.1 29.1 4 24 4c-7.5 0-13.9 4.3-17.7 10.7z"/>
                    <path fill="#4CAF50" d="M24 44c5 0 9.6-1.9 13-5l-6-4.9c-2 1.4-4.5 2.3-7 2.3-5 0-9.4-3.4-10.9-8.1l-6.6 5.1C8.3 39.6 15.6 44 24 44z"/>
                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1.3 3.6-5 6-9.3 6-5 0-9.4-3.4-10.9-8.1l-6.6 5.1C10.4 39.6 17.7 44 26 44c10.4 0 19-8.4 19-19 0-1.3-.1-2.2-.4-3.5z"/>
                  </svg>
                  Sign in with Google
                </button>
              </div>
            )}
            <p className="auth-modal-trust">Free to start · No credit card · Takes under a minute</p>
          </div>
        </Modal>
      </header>
    </>
  );
};

export default Header;
